/*
 * Frame selector ("rammevælger") — inline, Desenio-style frame picker for poster PDPs.
 *
 * - Swatches: "Ingen ramme" + every configured frame that has a variant in the selected
 *   poster size (oversized sizes only when <html data-oversized-frames="true">).
 * - Add-on panel when a frame is chosen: included framing service + hanger checkbox,
 *   or an "unframed" note for unframed sizes (e.g. 100x140). In a size without any frame
 *   (none made, or oversized and not allowed in this market) only "Ingen ramme" shows,
 *   with a note saying why; the selector never disappears.
 * - Prices: drives custom.js' window.frameSelection API so every existing price display
 *   (ATC, main price, sticky bar) shows poster + add-ons. Falls back to writing the ATC
 *   totals itself when custom.js is not on the page.
 * - Add to cart: when a frame is chosen, intercepts the product form submit (main form and
 *   sticky buy bar) and adds poster + frame (+ hanger) in ONE /cart/add.js items[] call,
 *   then dispatches `ajaxProduct:added` so the theme opens its cart drawer.
 * - Media: does NOT composite images. Dispatches `peleton:frame-change` {frame: key} and
 *   calls window.PeletonFrames.setFrame(root, key) when that component exists.
 *
 * Config comes from snippets/frame-selector.liquid — no shop-specific data in this file.
 */
(() => {
  if (customElements.get('frame-selector')) return;

  const norm = (s) =>
    String(s == null ? '' : s)
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/cm$/, '');

  const toSet = (csv) =>
    new Set(
      String(csv || '')
        .split(',')
        .map(norm)
        .filter(Boolean)
    );

  const parseKeys = (csv) => {
    const map = {};
    String(csv || '')
      .split(',')
      .forEach((pair) => {
        const [handle, key] = pair.split(':').map((s) => (s || '').trim());
        if (handle && key) map[handle] = key;
      });
    return map;
  };

  class FrameSelector extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-frame-selector-config]').textContent);
      } catch (e) {
        console.error('[frame-selector] invalid config', e);
        return;
      }

      const c = this.config;
      this.hangerExcluded = toSet(c.hangerExcludedSizes);
      this.unframedSizes = toSet(c.unframedSizes);
      this.oversizedSizes = toSet(c.oversizedSizes);
      this.colourKeys = parseKeys(c.colourKeys);
      this.framesByHandle = {};
      (c.frames || []).forEach((f) => (this.framesByHandle[f.handle] = f));

      this.swatches = Array.from(this.querySelectorAll('[data-frame]'));
      this.currentEl = this.querySelector('[data-current]');
      this.panel = this.querySelector('[data-panel]');
      this.serviceRow = this.querySelector('[data-service]');
      this.glassEl = this.querySelector('[data-glass]');
      this.hangerRow = this.querySelector('[data-hanger-row]');
      this.hangerInput = this.querySelector('[data-hanger]');
      this.hangerPriceEl = this.querySelector('[data-hanger-price]');
      this.unframedRow = this.querySelector('[data-unframed]');
      this.noFramesRow = this.querySelector('[data-no-frames]');
      this.noFramesText = this.querySelector('[data-no-frames-text]');
      this.errorEl = this.querySelector('[data-error]');

      this.selected = 'none';
      // 'none' on load keeps the default composite (oak) as the first image;
      // media only follows once the customer actually picks a frame.
      this.lastKey = 'none';
      this.variantId = null;

      this.swatches.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          this.select(btn.dataset.frame, true);
        });
      });
      this.querySelector('[data-swatches]').addEventListener('keydown', this.onKeydown.bind(this));
      this.hangerInput?.addEventListener('change', () => this.update());

      document.addEventListener('variant:changed', (e) => {
        const id = e.detail && (e.detail.variantId || (e.detail.variant && e.detail.variant.id));
        if (id) this.variantId = Number(id);
        // Run after every other listener (custom.js updates priceState synchronously).
        setTimeout(() => this.update(), 0);
      });

      // Capture phase: runs before the theme's <product-form> submit handler.
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('submit', this.onSubmit, true);

      const init = () =>
        setTimeout(() => {
          if (this.config.preselectFirst) {
            const first = this.visibleFrames()[0];
            if (first) this.selected = first;
          }
          this.update();
        }, 0);
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
      else init();
    }

    /* ---------- data ---------- */

    productForm() {
      return document.getElementById(this.dataset.productFormId);
    }

    posterVariant() {
      const variants = this.config.posterVariants || [];
      let id = this.variantId;
      if (!id) {
        const input = this.productForm()?.querySelector('[name="id"]');
        id = input ? Number(input.value) : null;
      }
      return variants.find((v) => v.id === id) || variants[0] || null;
    }

    oversizedAllowed() {
      return document.documentElement.dataset.oversizedFrames === 'true';
    }

    frameVariant(handle, size) {
      const frame = this.framesByHandle[handle];
      if (!frame) return null;
      const s = norm(size);
      if (this.oversizedSizes.has(s) && !this.oversizedAllowed()) return null;
      return frame.variants.find((v) => norm(v.size) === s) || null;
    }

    // An oversized size that frames exist in, but this market cannot get (kerbside freight).
    oversizedBlocked(size) {
      const s = norm(size);
      if (!this.oversizedSizes.has(s) || this.oversizedAllowed()) return false;
      return (this.config.frames || []).some((f) => f.variants.some((v) => norm(v.size) === s));
    }

    visibleFrames() {
      const pv = this.posterVariant();
      if (!pv) return [];
      return (this.config.frames || [])
        .filter((f) => {
          const v = this.frameVariant(f.handle, pv.size);
          return v && v.available;
        })
        .map((f) => f.handle);
    }

    /* ---------- formatting ---------- */

    money(cents) {
      let out;
      const fmt = (window.theme && theme.settings && theme.settings.moneyFormat) || this.config.moneyFormat;
      if (window.Woolman && Woolman.Utils && typeof Woolman.Utils.formatMoney === 'function') {
        try {
          out = Woolman.Utils.formatMoney(cents, fmt);
        } catch (e) {
          out = null;
        }
      }
      if (!out) {
        const amount = (cents / 100).toFixed(2);
        out = String(fmt || '{{amount_with_comma_separator}} kr')
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
          .replace(/\{\{\s*amount\s*\}\}/, amount);
      }
      // Match the theme's money_without_trailing_zeros ("199 kr", not "199,00 kr").
      return String(out).replace(/[.,]00(?=\D*$)/, '');
    }

    sizeLabel(size) {
      return window.peletonSizeLabel(size, 'short');
    }

    /* ---------- state ---------- */

    select(handle, userInitiated) {
      this.selected = handle;
      this.update();
      if (userInitiated) this.clearError();
    }

    onKeydown(e) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      const enabled = this.swatches.filter((b) => !b.hidden && b.getAttribute('aria-disabled') !== 'true');
      if (!enabled.length) return;
      e.preventDefault();
      let i = enabled.findIndex((b) => b.dataset.frame === this.selected);
      if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = enabled.length - 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') i = (i - 1 + enabled.length) % enabled.length;
      else i = (i + 1) % enabled.length;
      this.select(enabled[i].dataset.frame, true);
      enabled[i].focus();
    }

    update() {
      const pv = this.posterVariant();
      if (!pv) return;
      const size = pv.size;
      let anyFrame = false;

      this.swatches.forEach((btn) => {
        const handle = btn.dataset.frame;
        if (handle === 'none') return;
        const v = this.frameVariant(handle, size);
        const priceEl = btn.querySelector('[data-price]');
        if (!v) {
          btn.hidden = true;
          return;
        }
        btn.hidden = false;
        anyFrame = true;
        const soldOut = !v.available;
        btn.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
        btn.classList.toggle('is-sold-out', soldOut);
        if (soldOut) {
          priceEl.textContent = this.config.strings.soldOut || '';
        } else if (v.compare && v.compare > v.price) {
          const pct = Math.round((v.compare - v.price) / v.compare * 100);
          priceEl.innerHTML = `<span class="frame-selector__sale">+${this.money(v.price)}</span> <span class="frame-selector__pct">-${pct}%</span>`;
        } else {
          priceEl.textContent = `+${this.money(v.price)}`;
        }
      });

      // Selected frame no longer offered (or sold out) in this size → back to "Ingen ramme".
      if (this.selected !== 'none') {
        const v = this.frameVariant(this.selected, size);
        if (!v || !v.available) this.selected = 'none';
      }

      // Nothing to offer in this size (no frame made in it, or an oversized size this market
      // cannot get): keep the selector, show "Ingen ramme" as the only (selected) option and
      // say why. A vanished block reads as a broken page.
      this.removeAttribute('data-hidden');
      const noFrames = !anyFrame;
      if (noFrames) this.selected = 'none';
      // Only "Ingen ramme" left: CSS collapses the empty price line and the gap above the note.
      this.toggleAttribute('data-no-frames-mode', noFrames);

      this.swatches.forEach((btn) => {
        const on = btn.dataset.frame === this.selected;
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        btn.tabIndex = on ? 0 : -1;
      });

      const frameVariant = this.selected === 'none' ? null : this.frameVariant(this.selected, size);
      const frame = frameVariant ? this.framesByHandle[this.selected] : null;

      if (this.currentEl) {
        this.currentEl.textContent = frame ? frame.title : this.config.strings.noFrame || '';
      }

      // Add-on panel
      const unframed = this.unframedSizes.has(norm(size));
      const hangerOffered = !!(frame && this.config.hanger && this.hangerRow && !unframed && !this.hangerExcluded.has(norm(size)));
      if (this.panel) this.panel.hidden = !frame && !noFrames;
      if (this.noFramesRow) {
        this.noFramesRow.hidden = !noFrames;
        if (noFrames && this.noFramesText) {
          // Texts live in data attributes (locale strings, already HTML-escaped by `t`).
          const d = this.noFramesText.dataset;
          this.noFramesText.textContent = (this.oversizedBlocked(size) && d.textOversized) || d.textGeneric || '';
          if (!this.noFramesText.textContent) this.noFramesRow.hidden = true;
        }
      }
      if (this.serviceRow) this.serviceRow.hidden = !frame || unframed;
      if (this.unframedRow) this.unframedRow.hidden = !frame || !unframed;
      if (this.hangerRow) this.hangerRow.hidden = !hangerOffered;
      if (this.hangerPriceEl && this.config.hanger) {
        this.hangerPriceEl.textContent = `+${this.money(this.config.hanger.price)}`;
      }
      if (this.glassEl) {
        const glass = frameVariant && frameVariant.glass;
        this.glassEl.hidden = !glass;
        // Sentence case after the prefix: "Med akrylglas", not "Med Akrylglas".
        const prefix = (this.config.strings.glassPrefix || '').trim();
        const g = glass ? String(glass).trim() : '';
        // German capitalises nouns ("Mit Acrylglas"), so keep the case there.
        const keepCase = /^de\b/i.test(document.documentElement.lang || '');
        const gText = keepCase ? g : g.charAt(0).toLocaleLowerCase('da') + g.slice(1);
        let text = prefix && g ? `${prefix} ${gText}` : g;
        // Acrylic glazing gets a reassuring suffix ("Med akrylglas, der ligner ægte glas").
        if (g && /akryl|acryl/i.test(g) && this.config.strings.acrylicSuffix) text += this.config.strings.acrylicSuffix;
        this.glassEl.textContent = text;
      }

      this.state = {
        poster: pv,
        frame: frameVariant,
        frameHandle: frame ? this.selected : null,
        hanger: hangerOffered && this.hangerInput && this.hangerInput.checked ? this.config.hanger : null,
      };

      this.syncPrices();
      this.syncMedia(size);
    }

    /* ---------- prices ---------- */

    syncPrices() {
      const { poster, frame, hanger } = this.state;
      const regular = (v) => (v ? (v.compare && v.compare > v.price ? v.compare : v.price) : 0);
      const addon = frame ? frame.price + (hanger ? hanger.price : 0) : 0;
      const addonRegular = frame ? regular(frame) + (hanger ? regular(hanger) : 0) : 0;

      const fs = window.frameSelection;
      const ps = window.priceState;
      if (fs && ps && typeof fs.setFrame === 'function') {
        ps.variantPrice = poster.price;
        ps.variantComparePrice = poster.compare || null;
        if (frame) {
          fs.setFrame({
            price: addon,
            comparePrice: addonRegular,
            handle: this.state.frameHandle,
            title: this.framesByHandle[this.state.frameHandle].title,
            variant: frame.size,
            id: frame.id,
          });
        } else if (fs.isFrameSelected) {
          fs.clearFrame();
        }
        // custom.js does not touch the sticky bar's submit button total.
        setTimeout(() => this.writeTotals('.the-buy-bar .product-form__submit, product-buy-bar .product-form__submit'), 30);
      } else {
        this.writeTotals('.product-form__submit');
      }
    }

    writeTotals(selector) {
      const { poster, frame, hanger } = this.state;
      const total = poster.price + (frame ? frame.price : 0) + (hanger ? hanger.price : 0);
      const regular = (v) => (v ? (v.compare && v.compare > v.price ? v.compare : v.price) : 0);
      const totalRegular = (poster.compare || poster.price) + regular(frame) + regular(hanger);
      document.querySelectorAll(selector).forEach((btn) => {
        const p = btn.querySelector('.total-price');
        const c = btn.querySelector('.total-compare-price');
        if (p) p.textContent = this.money(total);
        if (c) {
          const show = totalRegular > total;
          c.textContent = show ? this.money(totalRegular) : '';
          c.style.display = show ? '' : 'none';
        }
      });
    }

    /* ---------- media (frame-compositing workstream) ---------- */

    syncMedia(size) {
      const key = this.state.frameHandle ? this.colourKeys[this.state.frameHandle] || this.state.frameHandle : 'none';
      // Text blocks with an alternative "with frame" text (e.g. delivery time).
      const framed = key !== 'none';
      document.querySelectorAll('[data-frame-text]').forEach((el) => {
        el.hidden = (el.dataset.frameText === 'framed') !== framed;
      });
      if (key === this.lastKey) return;
      this.lastKey = key;
      document.dispatchEvent(
        new CustomEvent('peleton:frame-change', {
          detail: { frame: key, handle: this.state.frameHandle, size },
        })
      );
      const pf = window.PeletonFrames;
      if (pf && typeof pf.setFrame === 'function') {
        try {
          const root =
            document.querySelector('[data-framed-poster-root]') ||
            this.closest('.product-main, .product-section, section') ||
            document;
          pf.setFrame(root, key);
        } catch (e) {
          console.warn('[frame-selector] PeletonFrames.setFrame failed', e);
        }
      }
    }

    /* ---------- add to cart ---------- */

    isProductForm(form) {
      // NB: form.id is shadowed by <input name="id"> on product forms → use getAttribute.
      const formId = form && form.getAttribute && form.getAttribute('id');
      if (!form || form.tagName !== 'FORM' || formId === 'frame-form') return false;
      if (!form.querySelector('[name="id"]')) return false;
      if (formId && formId === this.dataset.productFormId) return true;
      return !!form.closest('product-buy-bar, .the-buy-bar');
    }

    async onSubmit(e) {
      const form = e.target;
      if (!this.isProductForm(form) || !this.state) return;
      if (!this.state.frame) {
        // No frame chosen: offer the frame popup as the upsell (it adds the poster itself,
        // with or without a frame). Without a popup on the page the theme's normal flow runs.
        // Only when a frame can actually be offered in this size and market; otherwise the
        // popup would show an empty or stale offer, so the theme adds the poster directly.
        const popup = document.querySelector('popup-modal#frame-popup');
        const size = this.state.poster && this.state.poster.size;
        const fp = window.PeletonFramePopup;
        const popupHasFrames = fp && typeof fp.hasFrames === 'function' ? fp.hasFrames(size) : true;
        if (popup && typeof popup.open === 'function' && this.visibleFrames().length && popupHasFrames) {
          e.preventDefault();
          e.stopImmediatePropagation();
          popup.open();
        }
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      const host = form.closest('product-form') || form;
      const btn = form.querySelector('[type="submit"]');
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;

      const posterId = Number(form.querySelector('[name="id"]').value);
      const poster = (this.config.posterVariants || []).find((v) => v.id === posterId) || this.state.poster;
      const frame = this.frameVariant(this.state.frameHandle, poster.size);
      if (!frame || !frame.available) {
        this.showError(host);
        return;
      }

      const qtyInput =
        form.querySelector('[name="quantity"]') ||
        (form.getAttribute('id') && document.querySelector(`[name="quantity"][form="${form.getAttribute('id')}"]`));
      const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

      const posterItem = { id: posterId, quantity };
      const props = {};
      new FormData(form).forEach((value, key) => {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && typeof value === 'string' && value !== '') props[m[1]] = value;
      });
      if (Object.keys(props).length) posterItem.properties = props;

      // The cart (snippets/cart-items.liquid) only recognises these fixed keys. The block setting
      // can get machine-translated per locale (fr: "_Affiche à encadrer"), which silently unlinks
      // the frame from its poster, so anything else falls back to the default key.
      const LINK_KEYS = ['_Poster to frame', '_Passepartout for', '_Accessory for'];
      const linkKey = LINK_KEYS.includes(this.config.linkProperty) ? this.config.linkProperty : '_Poster to frame';
      const items = [
        posterItem,
        { id: frame.id, quantity, properties: { [linkKey]: poster.sku || String(poster.id) } },
      ];
      const hangerOffered =
        this.config.hanger &&
        !this.unframedSizes.has(norm(poster.size)) &&
        !this.hangerExcluded.has(norm(poster.size));
      if (hangerOffered && this.hangerInput && this.hangerInput.checked) {
        // Linked to the poster like the frame, so the cart can show it under its poster.
        items.push({ id: this.config.hanger.id, quantity, properties: { '_Hanger for': poster.sku || String(poster.id) } });
      }

      this.setLoading(host, btn, true);
      this.clearError(host);
      try {
        const res = await fetch(((window.routes && routes.cart_add_url) || '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.description || json.message || '');
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', { detail: { variantId: posterId, quantity, items: json.items } })
        );
      } catch (err) {
        console.error('[frame-selector] add to cart failed', err);
        this.showError(host, err && err.message);
      } finally {
        this.setLoading(host, btn, false);
      }
    }

    setLoading(host, btn, on) {
      if (btn) {
        btn.classList.toggle('loading', on);
        if (on) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      }
      const overlay = host.querySelector('.loading-overlay');
      if (overlay) overlay.classList.toggle('hidden', !on);
    }

    showError(host, message) {
      const text = message || this.config.strings.error || '';
      const wrapper = host && host.querySelector && host.querySelector('.product-form__error-message-wrapper');
      const msg = wrapper && wrapper.querySelector('.product-form__error-message');
      if (wrapper && msg) {
        msg.textContent = text;
        wrapper.hidden = false;
        wrapper.removeAttribute('hidden');
      } else if (this.errorEl) {
        this.errorEl.textContent = text;
        this.errorEl.hidden = false;
      }
    }

    clearError(host) {
      if (this.errorEl) this.errorEl.hidden = true;
      const wrapper = host && host.querySelector && host.querySelector('.product-form__error-message-wrapper');
      if (wrapper) wrapper.setAttribute('hidden', '');
    }
  }

  customElements.define('frame-selector', FrameSelector);
})();
